import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthState, LoginCredentials, User } from '../../types';
import { authApi } from '../../api/auth';

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('access_token'),
  isAuthenticated: !!localStorage.getItem('access_token'),
  loading: false,
  error: null,
};

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials & { otp_token?: string; backup_token?: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials);
      
      // Check if MFA is required (backend sends 200 OK but with mfa_required: true)
      if (response.mfa_required) {
        return rejectWithValue({
          mfa_required: true,
          error: response.error || 'MFA token required'
        });
      }
      
      // Normal successful login (has access token)
      if (response.access && response.refresh) {
        localStorage.setItem('access_token', response.access);
        localStorage.setItem('refresh_token', response.refresh);
        return response;
      }
      
      // Something else went wrong
      return rejectWithValue(response.error || 'Login failed');
      
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.response?.data?.error || 'Login failed');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authApi.getCurrentUser();
      return {
        ...user,
        is_gestionnaire: user.role === 'gestionnaire'
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to get user');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.user) {
          state.user = {
            ...action.payload.user,
            is_gestionnaire: action.payload.user.role === 'gestionnaire'
          };
        }
        state.token = action.payload.access || null;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        // Handle both string errors and MFA object errors
        const payload = action.payload as any;
        if (typeof payload === 'string') {
          state.error = payload;
        } else if (payload?.error) {
          // For MFA required, don't set error in state since we handle it differently
          if (!payload.mfa_required) {
            state.error = payload.error;
          }
        } else {
          state.error = 'Login failed';
        }
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;